import type { ComponentProps, ComponentPropsWithoutRef, ReactNode } from 'react';
import { AppPage, AppPageHeader, AppPageSection } from './AppPage';
import { cn } from '../shared/ui/utils';

type AppPageInset = 'default' | 'flush';

export interface TemplateProps {
  children: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  className?: string;
  contentClassName?: string;
  headerClassName?: string;
  fillHeight?: boolean;
  inset?: AppPageInset;
}

interface TemplateRegionProps {
  children?: ReactNode;
  className?: string;
}

interface TemplateToolbarProps extends TemplateRegionProps, ComponentPropsWithoutRef<'section'> {}

interface TemplateStackProps extends TemplateRegionProps, ComponentPropsWithoutRef<'div'> {}

interface TemplateSplitProps extends TemplateRegionProps, ComponentPropsWithoutRef<'div'> {}
interface StickyRegionProps extends TemplateRegionProps, ComponentPropsWithoutRef<'div'> {
  zIndexClassName?: string;
}

interface TemplateMainProps extends TemplateRegionProps, ComponentPropsWithoutRef<'section'> {
  padded?: boolean;
  scroll?: boolean;
}

interface TemplateAsideProps extends TemplateRegionProps, ComponentPropsWithoutRef<'aside'> {
  padded?: boolean;
  scroll?: boolean;
}

function TemplateFrame({
  template,
  children,
  title,
  description,
  eyebrow,
  actions,
  className,
  contentClassName,
  headerClassName,
  fillHeight = false,
  inset = 'default',
}: TemplateProps & { template: 'workspace' | 'collection' | 'form' | 'content' }) {
  return (
    <AppPage
      template={template}
      inset={inset}
      fillHeight={fillHeight}
      className={className}
      contentClassName={contentClassName}
    >
      {title || description || eyebrow || actions ? (
        <AppPageHeader
          title={title}
          description={description}
          eyebrow={eyebrow}
          actions={actions}
          className={headerClassName}
        />
      ) : null}
      {children}
    </AppPage>
  );
}

export function WorkspaceTemplate(props: TemplateProps) {
  return <TemplateFrame template="workspace" fillHeight inset="flush" {...props} />;
}

export function CollectionTemplate(props: TemplateProps) {
  return <TemplateFrame template="collection" inset="flush" {...props} />;
}

export function FormTemplate(props: TemplateProps) {
  return <TemplateFrame template="form" inset="flush" {...props} />;
}

export function ContentTemplate(props: TemplateProps) {
  return <TemplateFrame template="content" {...props} />;
}

export function ConversationTemplate({ className, ...props }: TemplateProps) {
  return (
    <TemplateFrame
      template="workspace"
      {...props}
      fillHeight
      inset="flush"
      className={cn('qs-template-conversation', className)}
    />
  );
}

export function TemplateSection(props: ComponentProps<typeof AppPageSection>) {
  return <AppPageSection {...props} />;
}

export function TemplateToolbar({ children, className, ...props }: TemplateToolbarProps) {
  return <section className={cn('qs-template-toolbar', className)} {...props}>{children}</section>;
}

export function TemplateStack({ children, className, ...props }: TemplateStackProps) {
  return <div className={cn('qs-template-stack', className)} {...props}>{children}</div>;
}

export function TemplateSplit({ children, className, ...props }: TemplateSplitProps) {
  return <div className={cn('qs-template-split', className)} {...props}>{children}</div>;
}

export function StickyRegion({
  children,
  className,
  zIndexClassName = 'z-20',
  ...props
}: StickyRegionProps) {
  return (
    <div className={cn('qs-sticky-region sticky top-0', zIndexClassName, className)} {...props}>
      {children}
    </div>
  );
}

export function TemplateMain({ children, className, padded = false, scroll = false, ...props }: TemplateMainProps) {
  return (
    <section
      className={cn(
        'qs-template-panel qs-template-main',
        padded && 'qs-template-surface',
        scroll && 'qs-template-scroll',
        className
      )}
      {...props}
    >
      {children}
    </section>
  );
}

export function TemplateAside({ children, className, padded = false, scroll = false, ...props }: TemplateAsideProps) {
  return (
    <aside
      className={cn(
        'qs-template-panel qs-template-aside',
        padded && 'qs-template-surface',
        scroll && 'qs-template-scroll',
        className
      )}
      {...props}
    >
      {children}
    </aside>
  );
}
